/**
 * From the crew's **Upload** (Board 10), and the phone's **Photo** and
 * **Upload** quick actions (§19.2, lead round 5) — `docs/visit-page.md` §7.9,
 * §27.4: the Files tab's picker; the file lands on today's day (or the first
 * day) as internal, and an image also writes a `visit_photos` row, entered by
 * the office, so it shows in the feed (`CrewPort.addPhotos`'s stand-in while
 * `crew.photos` is off).
 *
 * The picking, the per-file tries and the refusals are `useRecordUpload`'s —
 * the one owner of every picker (design system §9 item 27); what this adds is
 * the one write that files the uploads on the visit (`POST /visits/:id/photos`).
 * **Photo** opens the camera where there is one (`capture`), Upload the
 * picker.
 */
import { useCallback, useRef, type ReactNode } from 'react'
import { useRecordUpload } from '../record/use-record-upload.js'
import { useVisitWrite, type VisitWriteResult } from './api.js'

export interface VisitUpload {
  openPicker: (options?: { photo?: boolean }) => void
  /** The hidden input — render it once, anywhere on the page. */
  input: ReactNode
  busy: boolean
  /** The files that did not land, named. */
  error: string | null
}

export function useVisitUpload(
  visitId: string,
  orgUnitId: string,
  onWritten?: (result: VisitWriteResult) => void,
  tab: 'from-crew' | 'overview' = 'from-crew',
): VisitUpload {
  const write = useVisitWrite(visitId)
  const holder = useRef<HTMLSpanElement>(null)
  const upload = useRecordUpload({
    filesAt: null,
    orgUnitId,
    canEdit: true,
    describe: false,
    onUploaded: (files) => {
      if (files.length === 0) return
      void write('POST', '/photos', { fileIds: files.map((file) => file.id) }, { tab })
        .then((result) => onWritten?.(result))
    },
  })

  const { openPicker: open } = upload
  const openPicker = useCallback((options: { photo?: boolean } = {}) => {
    const input = holder.current?.querySelector('input')
    if (input) {
      if (options.photo) {
        input.setAttribute('accept', 'image/*')
        input.setAttribute('capture', 'environment')
      } else {
        input.removeAttribute('accept')
        input.removeAttribute('capture')
      }
    }
    open()
  }, [open])

  return {
    openPicker,
    input: <span ref={holder} className="hidden"><input {...upload.inputProps} /></span>,
    busy: upload.busy,
    error: upload.error,
  }
}
